import { useEffect, useRef, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import Lenis from "lenis";
import { SiteProvider, useSite } from "@/context/SiteContext";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Services from "@/components/Services";
import Gallery from "@/components/Gallery";
import Testimonials from "@/components/Testimonials";
import HoursLocation from "@/components/HoursLocation";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import AdminPanel from "@/components/AdminPanel";
import { imgUrl } from "@/api";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                        <p className="font-serif text-3xl text-gold-gradient">J&S Beauty Studio</p>
                        <p className="mt-4 text-zinc-400 font-light">Algo salió mal. Recarga la página para continuar.</p>
                        <button
                            onClick={() => window.location.reload()}
                            data-testid="error-reload-button"
                            className="mt-6 bg-gold-gradient text-black font-semibold px-6 py-2.5 rounded-full"
                        >
                            Recargar
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const Splash = () => (
    <div className="min-h-screen bg-[#0A0A0B] flex flex-col items-center justify-center gap-6" data-testid="loading-splash">
        <img src={imgUrl("/logo.jpg")} alt="J&S Beauty Studio" className="h-24 w-24 rounded-full object-cover ring-1 ring-gold/40 animate-pulse" />
        <p className="font-serif text-xl tracking-[0.3em] uppercase text-gold-light">J&S Beauty Studio</p>
    </div>
);

function Home() {
    const { content, loading } = useSite();

    if (loading || !content) return <Splash />;

    return (
        <main id="inicio" className="relative bg-[#0A0A0B]">
            <Header />
            <Hero />
            <Marquee />
            <Services />
            <Gallery />
            <Testimonials />
            <HoursLocation />
            <Contact />
            <Footer />
            <FloatingWhatsApp />
            <AdminPanel />
        </main>
    );
}

function App() {
    const lenisRef = useRef(null);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        const onClick = (e) => {
            const a = e.target.closest?.('a[href^="#"]');
            if (!a) return;
            const href = a.getAttribute("href");
            if (!href || href === "#") return;
            const el = document.querySelector(href);
            if (el) {
                e.preventDefault();
                lenis.scrollTo(el, { offset: -70, duration: 1.4 });
            }
        };
        document.addEventListener("click", onClick);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            document.removeEventListener("click", onClick);
        };
    }, []);

    return (
        <SiteProvider>
            <ErrorBoundary>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster richColors position="top-right" theme="dark" toastOptions={{ style: { background: "#121215", border: "1px solid rgba(212,175,55,0.3)", color: "#F4F4F5" } }} />
            </ErrorBoundary>
        </SiteProvider>
    );
}

export default App;
